import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useUserProfile } from "../../contexts/UserProfileContext";
import MultiSelectInput from "../../components/form/MultiSelectInput";

interface Item {
  id: string;
  name: string;
  category_id: string | null;
  created_at: string | null;
}

interface Category {
  id: string;
  name: string;
}

interface Tag {
  id: string;
  name: string;
}

interface ItemWithTags extends Item {
  tags: Tag[];
}

export default function PacklistItemLibraryAdmin() {
  const { profile } = useUserProfile();
  const [items, setItems] = useState<ItemWithTags[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ name: "", category_id: "" });
  const [editMode, setEditMode] = useState<string | null>(null);
  const [editData, setEditData] = useState<{
    name: string;
    category_id: string | null;
    tags: Tag[];
  }>({ name: "", category_id: "", tags: [] });

  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [filterTags, setFilterTags] = useState<Tag[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState("");
  const [collapsedCategories, setCollapsedCategories] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (profile?.role !== "admin") return;

    const fetchData = async () => {
      const [itemRes, catRes, tagRes] = await Promise.all([
        supabase
          .from("packlist_items")
          .select(
            "id, name, category_id, created_at, packlist_item_tags(packlist_tags(id, name))"
          )
          .order("created_at", { ascending: false }),
        supabase.from("packlist_categories").select("id, name"),
        supabase.from("packlist_tags").select("id, name"),
      ]);

      const itemData = itemRes.data?.map((item) => ({
        id: item.id,
        name: item.name,
        category_id: item.category_id,
        created_at: item.created_at,
        tags:
          item.packlist_item_tags?.map((link: any) => link.packlist_tags) || [],
      })) as ItemWithTags[];

      if (itemData) setItems(itemData);
      if (catRes.data) setCategories(catRes.data);
      if (tagRes.data) setAllTags(tagRes.data);
      setLoading(false);
    };

    fetchData();
  }, [profile]);

  const handleAdd = async () => {
    if (!newItem.name || !newItem.category_id) return;

    const { data, error } = await supabase
      .from("packlist_items")
      .insert([{ ...newItem }])
      .select();

    if (error) {
      alert("Fehler beim Hinzufügen: " + error.message);
    } else {
      const insertedItem = data?.[0];
      setItems([
        ...items,
        {
          ...insertedItem,
          tags: selectedTags ?? [], // <- Immer explizit ein Array mitgeben
        },
      ]);

      if (insertedItem && selectedTags.length > 0) {
        const tagLinks = selectedTags.map((tag) => ({
          item_id: insertedItem.id,
          tag_id: tag.id,
        }));
        await supabase.from("packlist_item_tags").insert(tagLinks);
      }

      setNewItem({ name: "", category_id: "" });
      setSelectedTags([]);
      setSuccessMessage(`„${newItem.name}" erfolgreich hinzugefügt ✅`);
      setTimeout(() => setSuccessMessage(null), 4000); // verschwindet nach 4 Sekunden
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Wirklich löschen?")) return;

    const { error } = await supabase
      .from("packlist_items")
      .delete()
      .eq("id", id);

    if (error) {
      alert("Fehler beim Löschen: " + error.message);
    } else {
      setItems(items.filter((i) => i.id !== id));
    }
  };

  const startEdit = (item: ItemWithTags) => {
    setEditMode(item.id);
    setEditData({
      name: item.name,
      category_id: item.category_id,
      tags: item.tags || [],
    });
  };

  const cancelEdit = () => setEditMode(null);

  const saveEdit = async (id: string) => {
    const { name, category_id, tags } = editData;

    const { error: updateError } = await supabase
      .from("packlist_items")
      .update({ name, category_id })
      .eq("id", id);

    if (updateError) {
      alert("Fehler beim Speichern: " + updateError.message);
      return;
    }

    // Tags aktualisieren: alte löschen, neue einfügen
    await supabase.from("packlist_item_tags").delete().eq("item_id", id);

    if (tags.length > 0) {
      const tagLinks = tags.map((tag) => ({
        item_id: id,
        tag_id: tag.id,
      }));
      await supabase.from("packlist_item_tags").insert(tagLinks);
    }

    // Lokalen Zustand aktualisieren
    const updatedItems = items.map((i) =>
      i.id === id ? { ...i, name, category_id, tags } : i
    );
    setItems(updatedItems);
    setEditMode(null);
  };

  const getCategoryName = (id: string | null) =>
    (id ? categories.find((c) => c.id === id)?.name : undefined) || "-";

  const filteredItems = items
    .filter((item) =>
      item.name.toLowerCase().includes(nameFilter.trim().toLowerCase())
    )
    .filter((item) =>
      filterTags.length === 0
        ? true
        : filterTags.every((filterTag) =>
            item.tags.some((itemTag) => itemTag.id === filterTag.id)
          )
    );

  return (
    <div className="max-w-4xl mx-auto mt-10 p-6">
      <h1 className="text-2xl font-bold text-sky-400 mb-6">
        📦 Zentrale Item-Verwaltung
      </h1>

      <div className="bg-[#161b27] border border-[#2a3347] rounded-xl p-4 mb-6">
        <h2 className="font-semibold mb-2 text-slate-100">➕ Neuen Item hinzufügen</h2>

        <div className="flex flex-col gap-3">
          <input
            type="text"
            placeholder="Item-Name"
            value={newItem.name}
            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
            className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-2 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
          />
          <select
            value={newItem.category_id}
            onChange={(e) =>
              setNewItem({ ...newItem, category_id: e.target.value })
            }
            className="border border-[#2a3347] bg-[#0d1117] text-slate-200 p-2 rounded outline-none focus:border-sky-400"
          >
            <option value="">Kategorie wählen</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
          <MultiSelectInput
            label="Tags"
            options={allTags.map((t) => t.name)}
            selected={editData.tags.map((t) => t.name)}
            isSaving={false}
            onChange={(newSelected: string[]) => {
              const tagObjects = allTags.filter((tag) =>
                newSelected.includes(tag.name)
              );
              setEditData({ ...editData, tags: tagObjects });
            }}
          />

          <button
            onClick={handleAdd}
            className="bg-sky-500 text-white px-4 py-2 rounded hover:bg-sky-600 transition-colors"
          >
            Item hinzufügen
          </button>
          {successMessage && (
            <p className="text-sm text-emerald-400 mt-2">{successMessage}</p>
          )}
        </div>
      </div>

      <h2 className="font-semibold mb-2 text-slate-100">📋 Bestehende Items</h2>
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-400 mb-1">
          🔍 Nach Name suchen...
        </label>
        <input
          type="text"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          className="border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded px-3 py-2 w-full outline-none focus:border-sky-400 placeholder:text-slate-600 text-sm"
        />
      </div>

      <div className="mb-4">
        <MultiSelectInput
          label={
            <span className="text-sm font-medium text-slate-400">
              🔍 Nach Tags filtern...
            </span>
          }
          options={allTags.map((t) => t.name)}
          selected={filterTags.map((t) => t.name)}
          isSaving={false}
          onChange={(selected: string[]) => {
            const matchingTags = allTags.filter((tag) =>
              selected.includes(tag.name)
            );
            setFilterTags(matchingTags);
          }}
        />
      </div>
      <div className="flex gap-4 mb-4">
        <button
          onClick={() =>
            setCollapsedCategories(
              Object.fromEntries(categories.map((cat) => [cat.name, false]))
            )
          }
          className="text-sm text-sky-400 hover:text-sky-300 transition-colors"
        >
          🔽 Alle aufklappen
        </button>
        <button
          onClick={() =>
            setCollapsedCategories(
              Object.fromEntries(categories.map((cat) => [cat.name, true]))
            )
          }
          className="text-sm text-sky-400 hover:text-sky-300 transition-colors"
        >
          🔼 Alle zuklappen
        </button>
      </div>

      {loading ? (
        <p className="text-slate-400">Lade Daten...</p>
      ) : items.length === 0 ? (
        <p className="text-slate-500">Keine Items vorhanden.</p>
      ) : (
        <ul className="space-y-3">
          {Object.entries(
            filteredItems
              .sort((a, b) =>
                a.name.localeCompare(b.name, "de", { sensitivity: "base" })
              )
              .reduce((acc, item) => {
                const catName = getCategoryName(item.category_id);
                if (!acc[catName]) acc[catName] = [];
                acc[catName].push(item);
                return acc;
              }, {} as Record<string, ItemWithTags[]>)
          ).map(([categoryName, itemsInCategory]) => (
            <div key={categoryName} className="mb-6">
              <div
                className="flex items-center justify-between cursor-pointer group"
                onClick={() =>
                  setCollapsedCategories((prev) => ({
                    ...prev,
                    [categoryName]: !prev[categoryName],
                  }))
                }
              >
                <h3 className="text-lg font-semibold text-slate-300 mb-2 group-hover:text-sky-400 transition-colors">
                  {collapsedCategories[categoryName] ? "▲" : "▼"} {categoryName}
                </h3>
              </div>

              {!collapsedCategories[categoryName] && (
                <ul className="space-y-3">
                  {itemsInCategory.map((item) => (
                    <li
                      key={item.id}
                      className="bg-[#1e2639] border border-[#2a3347] p-3 rounded flex justify-between items-center"
                    >
                      {editMode === item.id ? (
                        <div className="flex flex-col gap-3 w-full">
                          <input
                            type="text"
                            value={editData.name}
                            onChange={(e) =>
                              setEditData({ ...editData, name: e.target.value })
                            }
                            className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-2 rounded outline-none focus:border-sky-400"
                          />
                          <select
                            value={editData.category_id ?? ""}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                category_id: e.target.value || null,
                              })
                            }
                            className="border border-[#2a3347] bg-[#0d1117] text-slate-200 p-2 rounded outline-none focus:border-sky-400"
                          >
                            <option value="">Kategorie wählen</option>
                            {categories.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                          </select>
                          <MultiSelectInput
                            label="Tags"
                            options={allTags.map((t) => t.name)}
                            selected={editData.tags.map((t) => t.name)}
                            isSaving={false}
                            onChange={(newSelected: string[]) => {
                              const tagObjects = allTags.filter((tag) =>
                                newSelected.includes(tag.name)
                              );
                              setEditData({ ...editData, tags: tagObjects });
                            }}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveEdit(item.id)}
                              className="bg-sky-500 text-white px-3 py-1 rounded hover:bg-sky-600 transition-colors"
                            >
                              Speichern
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="bg-[#1e2639] border border-[#2a3347] text-slate-300 px-4 py-2 rounded hover:bg-[#2a3347] transition-colors"
                            >
                              Abbrechen
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div>
                            <div className="font-semibold text-slate-100">{item.name}</div>
                            {item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {item.tags.map((tag) => (
                                  <span
                                    key={tag.id}
                                    className="bg-[#1e2639] text-slate-400 border border-[#2a3347] text-xs px-2 py-0.5 rounded-full"
                                  >
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => startEdit(item)}
                              className="text-sm bg-amber-500 text-white px-3 py-1 rounded hover:bg-amber-600 transition-colors"
                            >
                              Bearbeiten
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                            >
                              Löschen
                            </button>
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </ul>
      )}
    </div>
  );
}
